import * as React from 'react';
import {TicketDetailBySlugQuery, useTicketDetailBySlugFetchKey} from '../hooks/useTicketDetailBySlug';
import {ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import {TicketDetailSkeleton} from '../components/TicketDetailSkeleton';
import {TicketNotFound} from '../components/TicketNotFound';
import {MainNarrow, RouteErrorBoundary, TicketDetailContent} from '../components/TicketDetailContent';
import type {useTicketDetailBySlugQuery$data} from '../hooks/__generated__/useTicketDetailBySlugQuery.graphql';

/**
 * The ticket detail page, fetched by human-readable slug (`ACME-1042`).
 *
 * Only the query lives here — the page itself is `TicketDetailContent`, shared with the
 * id route. Add UI there, not here, so both ways into a ticket stay the same page.
 */
export function TicketDetailBySlugRoute({slug}: {slug: string}) {
	const {fetchKey, refetch} = useTicketDetailBySlugFetchKey();

	return (
		<RouteErrorBoundary key={fetchKey}>
			<ForecastQueryRenderer
				query={TicketDetailBySlugQuery}
				variables={{slug}}
				customLoader={() => (
					<MainNarrow>
						<TicketDetailSkeleton />
					</MainNarrow>
				)}
				redirectIfNotLoggedIn
				render={(relayProps: useTicketDetailBySlugQuery$data | null) => {
					if (!relayProps || !relayProps.viewer) return null;
					const detail = relayProps.viewer.ticketDetailBySlug;
					if (!detail) {
						return (
							<MainNarrow>
								<TicketNotFound />
							</MainNarrow>
						);
					}
					return (
						<TicketDetailContent
							ticketRef={detail.ticket}
							timeline={detail.timeline}
							attachments={detail.attachments}
							refetch={refetch}
							currentPersonId={relayProps.viewer.actualPersonId ?? ''}
						/>
					);
				}}
			/>
		</RouteErrorBoundary>
	);
}
